import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle2, FileSearch, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { getToken } from '../../services/tokenService';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const h = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const eur = (v) => Number(v || 0).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

const Reglages = () => {
  const [r, setR] = useState(null);
  useEffect(() => { axios.get(`${API}/preuves-virement/reglage`, h()).then(({ data }) => setR(data)).catch(() => {}); }, []);
  const changer = async (k) => {
    try {
      const { data } = await axios.put(`${API}/preuves-virement/reglage`, { ...r, [k]: !r[k] }, h());
      setR(data);
      toast.success('Réglage enregistré');
    } catch (e) { toast.error(e.response?.data?.detail || 'Réglage impossible'); }
  };
  if (!r) return null;
  return (
    <div className="flex flex-wrap gap-4 text-xs text-slate-700">
      <label className="flex items-center gap-2"><input type="checkbox" checked={r.obligatoire} onChange={() => changer('obligatoire')} data-testid="preuves-reglage-obligatoire" /> Preuve obligatoire pour terminer l&apos;étape paiement</label>
      <label className="flex items-center gap-2"><input type="checkbox" checked={r.auto} onChange={() => changer('auto')} data-testid="preuves-reglage-auto" /> Enregistrement automatique par Klarys si tout concorde</label>
    </div>
  );
};

export const PreuvesVirementAdmin = () => {
  const { user } = useAuth() || {};
  const [items, setItems] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [montants, setMontants] = useState({});
  const charger = useCallback(async () => {
    setChargement(true);
    try { setItems((await axios.get(`${API}/admin/preuves-virement`, h())).data.preuves || []); } catch { setItems([]); }
    setChargement(false);
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const decider = async (p, decision) => {
    try {
      await axios.post(`${API}/admin/preuves-virement/${p.id}/${decision}`, { montant: Number(montants[p.id] || p.concordance?.montant || 0) || null }, h());
      toast.success(decision === 'confirmer' ? 'Paiement et reçu enregistrés' : 'Preuve refusée');
      charger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action impossible'); }
  };
  const voir = async (p) => {
    try {
      const r = await axios.get(`${API}/comptabilite/documents/${p.ged_id}/download`, { ...h(), responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('Document indisponible'); }
  };

  return (
    <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="preuves-virement-admin">
      <div className="mb-3 flex items-center gap-3">
        <FileSearch className="h-5 w-5 text-[#0a3d62]" />
        <h3 className="flex-1 text-base font-bold text-[#0a3d62]">Preuves de virement à vérifier <span className="ml-1 rounded-full bg-amber-100 px-2 text-xs text-amber-800" data-testid="preuves-virement-compte">{items.length}</span></h3>
        <button type="button" onClick={charger} className="rounded p-1.5 hover:bg-slate-100" data-testid="preuves-virement-rafraichir"><RefreshCw className="h-4 w-4" /></button>
      </div>
      {(user?.role || '').toLowerCase() === 'super_admin' && <Reglages />}
      {chargement ? <Loader2 className="mt-3 h-5 w-5 animate-spin text-slate-400" /> : items.length === 0 ? (
        <p className="mt-3 text-sm text-slate-500">Aucune preuve en attente : Klarys a enregistré automatiquement celles qui concordaient.</p>
      ) : (
        <div className="mt-3 space-y-2">
          {items.map((p) => (
            <div key={p.id} className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 text-xs" data-testid={`preuve-admin-${p.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <b className="text-sm text-slate-900">{p.raison_sociale || '—'}</b>
                <span className="font-mono text-slate-500">{p.numero_police}</span>
                <span className="ml-auto text-slate-500">Lu par Klarys : <b>{eur(p.lecture?.montant)}</b> · {p.lecture?.date || 'date ?'} · réf. « {p.lecture?.reference || '—'} »</span>
              </div>
              <ul className="mt-1.5 list-disc pl-5 text-amber-900">{(p.motifs || []).map((m) => <li key={m}>{m}</li>)}</ul>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {p.ged_id && <button type="button" onClick={() => voir(p)} className="rounded border border-slate-300 bg-white px-2 py-1 hover:bg-slate-50" data-testid={`preuve-voir-${p.id}`}>Voir la preuve</button>}
                <input type="number" step="0.01" placeholder="Montant €" value={montants[p.id] ?? (p.concordance?.montant || '')} onChange={(e) => setMontants({ ...montants, [p.id]: e.target.value })}
                  className="w-28 rounded border border-slate-300 px-2 py-1" data-testid={`preuve-montant-${p.id}`} />
                <button type="button" onClick={() => decider(p, 'confirmer')} className="inline-flex items-center gap-1 rounded bg-emerald-600 px-2.5 py-1 font-semibold text-white hover:bg-emerald-700" data-testid={`preuve-confirmer-${p.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Enregistrer paiement + reçu</button>
                <button type="button" onClick={() => decider(p, 'rejeter')} className="inline-flex items-center gap-1 rounded border border-rose-300 bg-white px-2.5 py-1 text-rose-700 hover:bg-rose-50" data-testid={`preuve-rejeter-${p.id}`}><XCircle className="h-3.5 w-3.5" /> Refuser</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
